import type { MenuProps } from "../ui";
import { createElement, type KeyboardEvent, type MouseEvent } from "react";
import type { AppShellIconKey } from "./appShellIcons";
import { AppShellIcon } from "./AppShellIcon";
import { AppShellMenuLabel } from "./AppShellMenuLabel";
import type { AttentionTone } from "../ui/AttentionIndicator";


export type AppShellMenuItem = {
  key: string;
  label?: string;
  icon?: AppShellIconKey | null;
  type?: "group" | "divider";
  children?: AppShellMenuItem[];
  disabled?: boolean;
  danger?: boolean;
  onTitleClick?: () => void;
  /** Hinweis am Eintrag; mit `count` als Zahl (ab 100 „99+“), sonst als Punkt. */
  attention?: { tone: AttentionTone; label: string; count?: number };
};
export type AppShellNavigation = {
  items: AppShellMenuItem[];
  selectedKeys?: string[];
  openKeys?: string[];
  onOpenChange?: (openKeys: string[]) => void;
  onClick?: (info: { key: string; keyPath: string[]; domEvent: MouseEvent<Element> | KeyboardEvent<Element> }) => void;
  /** Frühere Menüoption; der Navigationsbaum öffnet Zweige immer per Klick. */
  triggerSubMenuAction?: "hover" | "click";
};

/** Keep one open root branch, including its nested submenus. A newly opened branch wins. */
export function accordionOpenKeys(items: AppShellMenuItem[], nextKeys: string[], previousKeys: string[] = []): string[] {
  const roots = new Map<string, string>();
  const visit = (entries: AppShellMenuItem[], root?: string) => {
    for (const item of entries) {
      if (!item.children) continue;
      if (item.type === "group") {
        visit(item.children, root);
        continue;
      }
      const branch = root ?? item.key;
      roots.set(item.key, branch);
      visit(item.children, branch);
    }
  };
  visit(items);
  const valid = nextKeys.filter(key => roots.has(key));
  const added = valid.filter(key => !previousKeys.includes(key));
  const branch = roots.get(added.at(-1) ?? valid.at(-1) ?? "");
  return valid.filter(key => roots.get(key) === branch);
}

/** UI is created exclusively here, from serializable navigation metadata and actions. */
export function renderAppShellMenuItems(items: AppShellMenuItem[]): MenuProps["items"] {
  return items.map(item => {
    if (item.type === "divider") return { key: item.key, type: "divider" as const };
    const common = { key: item.key, label: createElement(AppShellMenuLabel, { item }), icon: item.icon ? createElement(AppShellIcon, { name: item.icon }) : undefined, disabled: item.disabled, danger: item.danger };
    if (item.type === "group") return { key: item.key, type: "group" as const, label: common.label, children: renderAppShellMenuItems(item.children ?? []) };
    if (item.children) return { ...common, children: renderAppShellMenuItems(item.children) ?? [] };
    return common;
  });
}
